import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Slider = () => {
  const [sliders, setSliders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchSliders = async () => {
      try {
        const response = await apiService.getSliders();
        if (response.estado === 1) {
          setSliders(response.data.filter(item => item.activo === 1));
        }
      } catch (error) {
        console.error('Error cargando sliders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSliders();
  }, []);

  // Auto-play
  useEffect(() => {
    if (isPlaying && sliders.length > 1) {
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, 5000); // Cambia cada 5 segundos
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, currentIndex, sliders.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % sliders.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + sliders.length) % sliders.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  if (loading) {
    return (
      <section className="relative w-full h-[60vh] md:h-[70vh] lg:h-[80vh] bg-black flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-yellow-600 border-t-yellow-500 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </section>
    );
  }

  if (sliders.length === 0) {
    return null;
  }

  return (
    <section 
      className="relative w-full h-[60vh] md:h-[70vh] lg:h-[80vh] bg-black overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Slides */}
      <div className="relative w-full h-full">
        <AnimatePresence mode="wait">
          {sliders.map((slide, index) => {
            if (index !== currentIndex) return null;

            return (
              <motion.div
                key={slide.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 w-full h-full"
              >
                {/* Imagen o Video */}
                {slide.imagen_url_completa ? (
                  <div className="relative w-full h-full">
                    {/* Verificar si es video por la extensión */}
                    {slide.imagen_url_completa.match(/\.(mp4|webm|ogg)$/i) ? (
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover"
                      >
                        <source src={slide.imagen_url_completa} type="video/mp4" />
                      </video>
                    ) : (
                      <img
                        src={slide.imagen_url_completa}
                        alt={slide.titulo}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Overlay oscuro */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
                )}

                {/* Contenido del slide - Ajustado para no superponerse con botones */}
                <div className="absolute inset-0 flex items-center">
                  <div className="container mx-auto px-4 lg:px-8">
                    {/* Contenedor de texto con espaciado responsivo */}
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.3 }}
                      className="max-w-2xl"
                    >
                      {/* Ajuste responsivo para evitar superposición */}
                      <div className="lg:ml-10 lg:mr-10">
                        {slide.titulo && (
                          <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
                          >
                            {slide.titulo}
                          </motion.h2>
                        )}

                        {slide.subtitulo && (
                          <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed"
                          >
                            {slide.subtitulo}
                          </motion.p>
                        )}

                        {slide.enlace_url && (
                          <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                          >
                            <Link
                              to={slide.enlace_url}
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 py-3 rounded-full font-bold text-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-yellow-500/25"
                            >
                              <span>Más Información</span>
                              <ChevronRight className="w-5 h-5" />
                            </Link>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Botones de navegación - Posicionados para no superponerse con el texto */}
      {sliders.length > 1 && (
        <>
          {/* Botón Anterior - Ajustado para móvil */}
          <motion.button
            onClick={prevSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="absolute left-4 sm:left-6 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-black/50 backdrop-blur-sm border-2 border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 hover:border-yellow-500 transition-all duration-300"
            aria-label="Slide anterior"
          >
            <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
          </motion.button>

          {/* Botón Siguiente - Ajustado para móvil */}
          <motion.button
            onClick={nextSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="absolute right-4 sm:right-6 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-black/50 backdrop-blur-sm border-2 border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 hover:border-yellow-500 transition-all duration-300"
            aria-label="Slide siguiente"
          >
            <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
          </motion.button>
        </>
      )}

      {/* Indicadores de progreso - Mejor posicionados */}
      {sliders.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {/* Dots - Ajustados para no superponerse con botones */}
          <div className="flex gap-2">
            {sliders.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-8 h-3 bg-yellow-500'
                    : 'w-3 h-3 bg-yellow-600/40 hover:bg-yellow-600/60'
                }`}
                aria-label={`Ir al slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Botón Play/Pause - Ajustado */}
          <button
            onClick={togglePlayPause}
            className="ml-3 w-10 h-10 bg-black/50 backdrop-blur-sm border border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 transition-all duration-300"
            aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* Barra de progreso - Mejor posicionada */}
      {sliders.length > 1 && isPlaying && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gray-800 z-20">
          <motion.div
            key={currentIndex}
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 5, ease: 'linear' }}
            className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600"
          />
        </div>
      )}

      {/* Gradientes decorativos */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-600/10 rounded-full filter blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-600/10 rounded-full filter blur-3xl pointer-events-none"></div>
    </section>
  );
};

export default Slider;